'use client';

import type { ProductDocument, ProductVariantDocument } from '@/types/manage.types';
import { IProductRepository } from '../../domain/repositories/IProductRepository';
import { FirebaseProductRepository } from './FirebaseProductRepository';
import { CacheService, MemoryCache } from '../services/CacheService';

/**
 * Cached wrapper for FirebaseProductRepository
 * Provides intelligent caching with proper cache invalidation
 */
export class CachedFirebaseProductRepository implements IProductRepository {
  private productCache: MemoryCache<ProductDocument>;
  private variantCache: MemoryCache<ProductVariantDocument>;
  private listCache: MemoryCache<ProductDocument[]>;
  private categoryCache: MemoryCache<string[]>;

  constructor(private baseRepository: FirebaseProductRepository = new FirebaseProductRepository()) {
    // Configure caches with appropriate TTLs
    this.productCache = CacheService.getCache('products', {
      ttl: 10 * 60 * 1000, // 10 minutes for individual products
      maxSize: 200,
      keyPrefix: 'product',
    });

    this.variantCache = CacheService.getCache('variants', {
      ttl: 5 * 60 * 1000, // 5 minutes for variants (change more frequently)
      maxSize: 500,
      keyPrefix: 'variant',
    });

    this.listCache = CacheService.getCache('product_lists', {
      ttl: 2 * 60 * 1000, // 2 minutes for lists (to stay fresh)
      maxSize: 50,
      keyPrefix: 'list',
    });

    this.categoryCache = CacheService.getCache('categories', {
      ttl: 15 * 60 * 1000, // 15 minutes for categories (change rarely)
      maxSize: 10,
      keyPrefix: 'category',
    });
  }

  // Product operations with caching
  async findProductById(productId: string): Promise<ProductDocument | null> {
    return this.productCache.getOrSet(`product_${productId}`, () => this.baseRepository.findProductById(productId));
  }

  async findAllProducts(limit?: number): Promise<ProductDocument[]> {
    const cacheKey = `all_products_${limit || 'unlimited'}`;
    return this.listCache.getOrSet(cacheKey, () => this.baseRepository.findAllProducts(limit));
  }

  async findProductsByCategory(category: string): Promise<ProductDocument[]> {
    const cacheKey = `category_${category}`;
    return this.listCache.getOrSet(cacheKey, () => this.baseRepository.findProductsByCategory(category));
  }

  async saveProduct(product: ProductDocument): Promise<void> {
    await this.baseRepository.saveProduct(product);

    // Invalidate related caches
    this.productCache.set(`product_${product.id}`, product);
    this.listCache.invalidateByPattern('all_products_');
    this.listCache.invalidateByPattern(`category_${product.category}`);
  }

  async deleteProduct(productId: string): Promise<void> {
    await this.baseRepository.deleteProduct(productId);

    // Invalidate caches
    this.productCache.delete(`product_${productId}`);
    this.listCache.clear(); // Clear all lists since we don't know the category
    this.variantCache.invalidateByPattern(`product_${productId}_`);
  }

  // Product variant operations with caching
  async findVariantById(variantId: string): Promise<ProductVariantDocument | null> {
    return this.variantCache.getOrSet(`variant_${variantId}`, () => this.baseRepository.findVariantById(variantId));
  }

  async findAllVariants(limit?: number): Promise<ProductVariantDocument[]> {
    const cacheKey = `all_variants_${limit || 'unlimited'}`;
    return this.listCache.getOrSet(cacheKey, () => this.baseRepository.findAllVariants(limit)) as Promise<
      ProductVariantDocument[]
    >;
  }

  async findVariantsByProductId(productId: string): Promise<ProductVariantDocument[]> {
    const cacheKey = `product_${productId}_variants`;
    return this.listCache.getOrSet(cacheKey, () => this.baseRepository.findVariantsByProductId(productId)) as Promise<
      ProductVariantDocument[]
    >;
  }

  async saveVariant(variant: ProductVariantDocument): Promise<void> {
    await this.baseRepository.saveVariant(variant);

    // Update caches
    this.variantCache.set(`variant_${variant.id}`, variant);
    this.listCache.delete(`product_${variant.product_id}_variants`);
    this.listCache.invalidateByPattern('all_variants_');
  }

  async deleteVariant(variantId: string): Promise<void> {
    // Get variant first to know which product to invalidate
    const variant = await this.findVariantById(variantId);

    await this.baseRepository.deleteVariant(variantId);

    // Invalidate caches
    this.variantCache.delete(`variant_${variantId}`);
    if (variant) {
      this.listCache.delete(`product_${variant.product_id}_variants`);
    }
    this.listCache.invalidateByPattern('all_variants_');
  }

  async updateVariantPrice(variantId: string, price: number): Promise<void> {
    await this.baseRepository.updateVariantPrice(variantId, price);

    // Invalidate variant cache to force refresh
    this.variantCache.delete(`variant_${variantId}`);
  }

  // Subscription operations (bypass cache for real-time data)
  subscribeToProducts(callback: (products: ProductDocument[]) => void, limit?: number): () => void {
    return this.baseRepository.subscribeToProducts((products) => {
      // Update cache with fresh data from subscription
      products.forEach((product) => {
        this.productCache.set(`product_${product.id}`, product);
      });

      // Clear list caches to force refresh
      this.listCache.clear();

      callback(products);
    }, limit);
  }

  subscribeToVariants(callback: (variants: ProductVariantDocument[]) => void, limit?: number): () => void {
    return this.baseRepository.subscribeToVariants((variants) => {
      // Update cache with fresh data from subscription
      variants.forEach((variant) => {
        this.variantCache.set(`variant_${variant.id}`, variant);
      });

      // Clear variant list caches
      this.listCache.invalidateByPattern('all_variants_');
      this.listCache.invalidateByPattern('product_.*_variants');

      callback(variants);
    }, limit);
  }

  // Utility operations with caching
  async getProductCategories(): Promise<string[]> {
    return this.categoryCache.getOrSet('all_categories', () => this.baseRepository.getProductCategories());
  }

  async searchProducts(query: string): Promise<ProductDocument[]> {
    // Don't cache search results as they're highly specific
    return this.baseRepository.searchProducts(query);
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats() {
    return {
      products: this.productCache.getStats(),
      variants: this.variantCache.getStats(),
      lists: this.listCache.getStats(),
      categories: this.categoryCache.getStats(),
    };
  }

  /**
   * Clear all caches - useful for debugging or manual refresh
   */
  clearAllCaches(): void {
    this.productCache.clear();
    this.variantCache.clear();
    this.listCache.clear();
    this.categoryCache.clear();
  }

  /**
   * Warm up cache with commonly accessed data
   */
  async warmUpCache(): Promise<void> {
    try {
      // Pre-load categories
      await this.getProductCategories();

      // Pre-load first page of products
      await this.findAllProducts(50);

      console.log('Product cache warmed up successfully');
    } catch (error) {
      console.warn('Failed to warm up product cache:', error);
    }
  }
}
